import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../lib/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useTagCombinations, useTrend, type TrendBucket, type TrendGroupBy } from "./api";
import { foldGroups } from "./trend";
import { TrendChart } from "./trend-chart";
import { ErrorBanner, Segmented } from "./parts";

/**
 * The Trend-Dashboard ([F-023]): "eine eigene Trend-Dashboard-Ansicht für die
 * Manager-Rolle" - its own view at /trends, where it used to be two sections
 * at the foot of the dashboard. (1) incidents over time per week, month or
 * quarter, grouped by category, area or tag (AC-022); (2) the top tag pairs
 * in the last N incidents, N chosen here (AC-023). Read only: the controls
 * choose what is shown and nothing else. Who sees it is `dashboard.trends`
 * ([F-027]); the server answers the same for every query.
 */

/** US-020: tag combinations need >= 10 tagged incidents to be meaningful. */
const TAG_COMBINATIONS_MIN_INCIDENTS = 10;

/** Top-N request size for the tag-combination list. */
const TAG_COMBINATIONS_LIMIT = 10;

/** "in den letzten N Vorfällen" - the api takes 10..500. */
const COMBO_WINDOWS = [50, 100, 200, 500] as const;
type ComboWindow = (typeof COMBO_WINDOWS)[number];

const BUCKETS: readonly TrendBucket[] = ["week", "month", "quarter"];
const GROUP_BYS: readonly TrendGroupBy[] = ["category", "area", "tag"];
const MONTH_RANGES = [6, 12, 24] as const;
type MonthRange = (typeof MONTH_RANGES)[number];

const BUCKET_LABEL_KEY: Record<TrendBucket, string> = {
  week: "dashboard.trend.bucketWeek",
  month: "dashboard.trend.bucketMonth",
  quarter: "dashboard.trend.bucketQuarter",
};

const GROUP_BY_LABEL_KEY: Record<TrendGroupBy, string> = {
  category: "dashboard.trend.groupCategory",
  area: "dashboard.trend.groupArea",
  tag: "dashboard.trend.groupTag",
};

const MONTHS_LABEL_KEY: Record<MonthRange, string> = {
  6: "dashboard.trend.range6",
  12: "dashboard.trend.range12",
  24: "dashboard.trend.range24",
};

export function TrendsPage(): JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();
  const isManager = can("dashboard.trends");

  const [bucket, setBucket] = useState<TrendBucket>("month");
  const [groupBy, setGroupBy] = useState<TrendGroupBy>("category");
  const [months, setMonths] = useState<MonthRange>(12);
  const [comboWindow, setComboWindow] = useState<ComboWindow>(100);

  const trend = useTrend({ bucket, groupBy, months }, { enabled: isManager });
  const combos = useTagCombinations(
    { limit: TAG_COMBINATIONS_LIMIT, window: comboWindow },
    { enabled: isManager },
  );

  /**
   * Color-slot registry per grouping dimension: feeding the previous slot map
   * back into foldGroups keeps a surviving group's color stable across time-
   * window changes; switching the dimension starts a fresh entity space.
   */
  const slotsRef = useRef<{
    dim: TrendGroupBy;
    slots: ReadonlyMap<string, number>;
  } | null>(null);
  const matrix = useMemo(() => {
    if (!trend.data) return null;
    const previous =
      slotsRef.current?.dim === groupBy ? slotsRef.current.slots : undefined;
    const folded = foldGroups(trend.data.items, 6, previous);
    slotsRef.current = { dim: groupBy, slots: folded.slots };
    return folded;
  }, [trend.data, groupBy]);

  if (!isManager) {
    return (
      <div
        data-testid="trends_forbidden"
        className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground"
      >
        {t("errors.forbidden")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 data-testid="trends_title" className="text-2xl font-semibold tracking-tight">
          {t("trends.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("trends.subtitle")}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
      <Card data-testid="dashboard_trend" className="lg:col-span-2">
        <CardHeader>
          <CardTitle data-testid="dashboard_trend-title">
            {t("dashboard.trend.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <Segmented
              testid="dashboard_bucket"
              label={t("dashboard.trend.bucketLabel")}
              value={bucket}
              options={BUCKETS.map((value) => ({
                value,
                label: t(BUCKET_LABEL_KEY[value]),
              }))}
              onChange={setBucket}
            />
            <Segmented
              testid="dashboard_grouping"
              label={t("dashboard.trend.groupLabel")}
              value={groupBy}
              options={GROUP_BYS.map((value) => ({
                value,
                label: t(GROUP_BY_LABEL_KEY[value]),
              }))}
              onChange={setGroupBy}
            />
            <div className="space-y-1.5">
              <div className="text-sm font-medium">
                {t("dashboard.trend.rangeLabel")}
              </div>
              <Select
                value={String(months)}
                // The items below only ever emit MONTH_RANGES values.
                onValueChange={(value) => setMonths(Number(value) as MonthRange)}
              >
                <SelectTrigger data-testid="dashboard_range" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_RANGES.map((value) => (
                    <SelectItem
                      key={value}
                      value={String(value)}
                      data-testid={`dashboard_range-option_${value}`}
                    >
                      {t(MONTHS_LABEL_KEY[value])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {trend.isError ? (
            <ErrorBanner section="trend" onRetry={() => void trend.refetch()} />
          ) : !matrix ? (
            <div
              data-testid="dashboard_trend-loading"
              className="h-64 animate-pulse rounded-lg bg-muted"
            />
          ) : (
            <TrendChart matrix={matrix} bucket={bucket} />
          )}
        </CardContent>
      </Card>

      <Card data-testid="dashboard_combos">
        <CardHeader className="space-y-3">
          <CardTitle data-testid="dashboard_combos-title">
            {t("dashboard.combos.title")}
          </CardTitle>
          {/* F-023 (2): "in den letzten N Vorfällen" - N is the manager's. */}
          <div className="space-y-1.5">
            <div className="text-sm font-medium">{t("trends.windowLabel")}</div>
            <Select
              value={String(comboWindow)}
              onValueChange={(value) => setComboWindow(Number(value) as ComboWindow)}
            >
              <SelectTrigger data-testid="trends_window" className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMBO_WINDOWS.map((value) => (
                  <SelectItem
                    key={value}
                    value={String(value)}
                    data-testid={`trends_window-option_${value}`}
                  >
                    {t("trends.windowOption", { count: value })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {combos.isError ? (
            <ErrorBanner section="combos" onRetry={() => void combos.refetch()} />
          ) : combos.isLoading || !combos.data ? (
            <div data-testid="dashboard_combos-loading" className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="h-6 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : combos.data.meta.taggedIncidents <
              TAG_COMBINATIONS_MIN_INCIDENTS ||
            combos.data.items.length === 0 ? (
            <p
              data-testid="dashboard_combos-too-few"
              className="text-sm text-muted-foreground"
            >
              {t("dashboard.combos.tooFew")}
            </p>
          ) : (
            <ol data-testid="dashboard_combos-list" className="divide-y">
              {combos.data.items.map((item) => {
                const comboKey = `${item.tagAId}-${item.tagBId}`;
                return (
                  <li
                    key={comboKey}
                    data-testid={`dashboard_combo_${comboKey}`}
                    className="flex items-baseline justify-between gap-3 py-2 text-sm"
                  >
                    <span className="font-medium">
                      {item.tagAName} + {item.tagBName}
                    </span>
                    <span className="whitespace-nowrap text-muted-foreground">
                      {item.count === 1
                        ? t("dashboard.combos.one")
                        : t("dashboard.combos.many", { count: item.count })}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
